import { parseWorkItemEstimateInputToMinutes } from "@/utils/workItemEstimateInput";
import { resolvePriorityIdForWorkItemApi } from "@/Pages/Issues/issueMappers";
import {
  remapWorkItemStatusForTargetProject,
  getProjectWorkflowStatusesList,
} from "@/constants/workItemStatusCategoryTheme";
import {
  buildWorkItemSubmitBody,
  buildBugDetailsFromFormFields,
} from "@/utils/buildWorkItemSubmitBody";

function parseNonNegativeInt(value, fallback = 0) {
  const n = Number.parseInt(String(value ?? "").trim(), 10);
  return Number.isFinite(n) && n >= 0 ? n : fallback;
}

/**
 * Fields the backlog edit form does not change but must send on PATCH (optimistic locking, status, etc.).
 */
export function extractUpdateBaselineFromWorkItem(wi) {
  if (wi == null || typeof wi !== "object") return null;
  const statusId =
    wi.statusId ??
    (typeof wi.status === "object" && wi.status != null ? wi.status.id : null) ??
    null;
  const statusCategory =
    wi.statusCategory ??
    (typeof wi.status === "object" && wi.status != null ? wi.status.category : null) ??
    null;
  const statusName =
    typeof wi.status === "string" ? wi.status : (wi.status?.name ?? wi.status?.category ?? "");
  const reporterId =
    wi.reporterId ??
    (typeof wi.reporter === "object" && wi.reporter != null ? wi.reporter.id : null) ??
    null;
  const originalEstimateMinutes = parseNonNegativeInt(
    wi.originalEstimateMinutes ?? wi.originalEstimateMinutesInMinutes,
    0
  );
  const remainingEstimateMinutes =
    wi.remainingEstimateMinutes != null
      ? parseNonNegativeInt(wi.remainingEstimateMinutes, originalEstimateMinutes)
      : originalEstimateMinutes;
  const sp =
    wi.storyPoints != null && String(wi.storyPoints).trim() !== "" ? Number(wi.storyPoints) : null;
  return {
    version: wi.version ?? 1,
    statusId,
    statusCategory,
    statusName,
    reporterId,
    progressPercent: parseNonNegativeInt(wi.progressPercent, 0),
    remainingEstimateMinutes,
    originalEstimateMinutes,
    storyPointsFromServer: sp != null && !Number.isNaN(sp) ? sp : null,
  };
}

/**
 * PATCH body — same shape as create POST; adds `version` from baseline.
 */
export function buildUpdateWorkItemBodyFromBacklogForm({
  data,
  description,
  baseline,
  typeOptions,
  priorityList = [],
  priorityOptions = [],
  targetProjectId = null,
  sourceProjectId = null,
  targetStatusesPayload = null,
}) {
  if (baseline == null) {
    throw new Error("baseline is required");
  }

  const estimateTrimmed = String(data.originalEstimate ?? "").trim();
  let parsedEstimateForCap = null;
  if (estimateTrimmed) {
    parsedEstimateForCap = parseWorkItemEstimateInputToMinutes(estimateTrimmed);
    if (!parsedEstimateForCap.ok) {
      return { ok: false, error: parsedEstimateForCap.error || "Invalid estimate." };
    }
  }

  const storyPointsValue =
    data.storyPoints != null && String(data.storyPoints).trim() !== ""
      ? Number(data.storyPoints)
      : baseline.storyPointsFromServer;

  const originalCapMinutes = parsedEstimateForCap
    ? parsedEstimateForCap.minutes
    : parseNonNegativeInt(baseline.originalEstimateMinutes, 0);
  const remainingEstimateMinutes =
    baseline.remainingEstimateMinutes != null
      ? parseNonNegativeInt(baseline.remainingEstimateMinutes, originalCapMinutes)
      : originalCapMinutes;

  const typeIdTrimmed =
    data.type != null && String(data.type).trim() !== "" ? String(data.type).trim() : null;

  const selectedLabel = typeOptions.find((o) => o.value === data.type)?.label ?? "";
  const isBugType = String(selectedLabel).trim().toLowerCase() === "bug";

  const target = targetProjectId != null ? String(targetProjectId).trim() : "";
  const source = sourceProjectId != null ? String(sourceProjectId).trim() : "";
  const isProjectMove = Boolean(target && source && target !== source);

  let statusIdForPatch = baseline.statusId ?? null;
  if (isProjectMove) {
    const remapped = remapWorkItemStatusForTargetProject(
      {
        statusCategory: baseline.statusCategory,
        statusId: baseline.statusId,
        status: baseline.statusName,
      },
      getProjectWorkflowStatusesList(targetStatusesPayload)
    );
    statusIdForPatch = remapped.statusId ?? statusIdForPatch;
  }

  const body = buildWorkItemSubmitBody({
    version: baseline.version ?? 1,
    typeId: typeIdTrimmed,
    typeLabel: selectedLabel,
    title: data.summary?.trim() ?? "",
    description:
      description != null && String(description).trim() !== "" ? String(description) : null,
    statusId: statusIdForPatch,
    priorityId:
      resolvePriorityIdForWorkItemApi(data.priority, priorityList, priorityOptions) || null,
    assigneeId: data.assignee?.trim() || null,
    parentId: data.parentId,
    originalEstimate: estimateTrimmed,
    remainingEstimateMinutes,
    storyPoints:
      storyPointsValue != null && !Number.isNaN(Number(storyPointsValue))
        ? Number(storyPointsValue)
        : 0,
    startDate: data.startDate,
    dueDate: data.dueDate,
    isBacklog: Boolean(data.isBacklog),
    reporterId: baseline.reporterId ?? null,
    progressPercent: baseline.progressPercent ?? 0,
    projectId: isProjectMove ? target : undefined,
    bugDetails: isBugType ? buildBugDetailsFromFormFields(data) : undefined,
  });

  return { ok: true, body };
}
